import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { toast } from 'sonner';
import { Users, UserPlus, Clock } from 'lucide-react';
import { useTeams } from '@/hooks/useTeams';

interface ParticipantManagerProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  teamId: number | null;
  onParticipantUpdate: () => void;
}

interface TeamMember {
  id: string;
  name: string;
  role: string;
  type: 'player' | 'coach';
  isSelected: boolean;
}

export const ParticipantManager: React.FC<ParticipantManagerProps> = ({
  open,
  onClose,
  eventId,
  teamId,
  onParticipantUpdate,
}) => {
  const { profile } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const { teams } = useTeams();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [participantCount, setParticipantCount] = useState(0);

  const currentUserId = profile?.id || internalUser?.id;

  useEffect(() => {
    if (open && teamId) {
      loadTeamMembers();
    }
  }, [open, teamId]);

  const loadTeamMembers = async () => {
    if (!teamId) return;

    try {
      setLoading(true);
      
      // For now, we'll use the teams data to get basic info
      // In a real implementation, you'd fetch detailed team member data
      const team = teams.find(t => t.team_id === teamId);
      if (!team) {
        setTeamMembers([]);
        return;
      }

      // Mock team members for demonstration
      // In a real app, you'd fetch from team_players and team_coaches tables
      const mockMembers: TeamMember[] = [
        {
          id: currentUserId || 'coach-1',
          name: 'Coach (You)',
          role: 'Head Coach',
          type: 'coach',
          isSelected: true,
        },
        // Add more mock data as needed
      ];

      setTeamMembers(mockMembers);
      setParticipantCount(mockMembers.filter(m => m.isSelected).length);
    } catch (error) {
      console.error('Error loading team members:', error);
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const handleMemberToggle = (memberId: string) => {
    setTeamMembers(prev => {
      const updated = prev.map(member => 
        member.id === memberId 
          ? { ...member, isSelected: !member.isSelected }
          : member
      );
      setParticipantCount(updated.filter(m => m.isSelected).length);
      return updated;
    });
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      // In a real implementation, you'd save to the database
      // For now, we'll just update the local count and close
      
      toast.success(`${participantCount} participants selected!`);
      onParticipantUpdate();
      onClose();
    } catch (error) {
      console.error('Error updating participants:', error);
      toast.error('Failed to update participants');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0d1117] border border-gray-800 text-white max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-sports-green flex items-center gap-2">
            <Users className="h-5 w-5" />
            Manage Participants
          </DialogTitle>
        </DialogHeader>

        {!teamId ? (
          <div className="text-center py-8 text-gray-400">
            <UserPlus className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Select a team when creating the event to manage participants</p>
          </div>
        ) : (
          <div className="space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sports-green"></div>
              </div>
            ) : teamMembers.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No team members found</p>
                <p className="text-sm mt-2">Team members will be added automatically when you build out your team roster.</p>
              </div>
            ) : (
              <>
                <div className="mb-4 p-3 bg-gray-900 rounded-lg border border-gray-700">
                  <p className="text-sm text-gray-400">Expected Participants</p>
                  <p className="text-lg font-semibold text-sports-green">{participantCount}</p>
                </div>
                
                <div className="space-y-2">
                  {teamMembers.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center justify-between p-3 bg-gray-900 rounded-lg border border-gray-700"
                    >
                      <div className="flex items-center space-x-3">
                        <Checkbox
                          checked={member.isSelected}
                          onCheckedChange={() => handleMemberToggle(member.id)}
                          className="data-[state=checked]:bg-sports-green data-[state=checked]:border-sports-green"
                        />
                        <div>
                          <p className="font-medium text-white">{member.name}</p>
                          <p className="text-sm text-gray-400">
                            {member.role} • {member.type}
                          </p>
                        </div>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        Expected
                      </Badge>
                    </div>
                  ))}
                </div>

                <div className="flex gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={loading}
                    className="flex-1 bg-sports-green text-black hover:bg-green-600"
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};